import {
  AloftPrice,
  MODEL_RELIABLE_R_SQUARED_MIN,
  MODEL_RELIABLE_STANDARD_ERROR_RATIO_MAX,
  RegressionOutputSuccess,
} from '@aloft/toolkit-shared';
import { Flex, ProductColor, Typography, colors } from '@aloft/toolkit-sui';

type QualityLabel = 'EXCELLENT' | 'SUFFICIENT' | 'INSUFFICIENT';

const getThresholdLabel = (
  value: number,
  excellent: number,
  sufficient: number,
  higherIsBetter = true,
): QualityLabel => {
  const passes = higherIsBetter
    ? (v: number, t: number) => v >= t
    : (v: number, t: number) => v <= t;
  if (passes(value, excellent)) return 'EXCELLENT';
  if (passes(value, sufficient)) return 'SUFFICIENT';
  return 'INSUFFICIENT';
};

const qualityColor = (label: QualityLabel): ProductColor => {
  if (label === 'EXCELLENT') return 'textSuccess';
  if (label === 'SUFFICIENT') return 'textPrimary';
  return 'textError';
};

const StatTile = ({
  label,
  value,
  quality,
}: {
  label: string;
  value: string;
  quality?: QualityLabel;
}) => (
  <Flex
    flexDirection="column"
    alignItems="center"
    justifyContent="center"
    gap="0.5rem"
    padding="1rem"
    backgroundColor="surface1"
    border={`1px solid ${colors.strokeDefault}`}
    borderRadius="8px"
    width="140px"
    height="100px"
    flexShrink={0}
  >
    <Typography variant="body2" color="textLight" textAlign="center">
      {label}
    </Typography>
    <Typography variant="h2">{value}</Typography>
    {quality && (
      <Typography variant="subtitle2" color={qualityColor(quality)}>
        {quality}
      </Typography>
    )}
  </Flex>
);

export const StatsRow = ({ output }: { output: RegressionOutputSuccess }) => (
  <Flex gap="1.5rem" justifyContent="center" flexWrap="wrap">
    <StatTile
      label="Sample Size"
      value={String(output.sampleSize)}
      quality={getThresholdLabel(output.sampleSize, 50, 30)}
    />
    <StatTile label="Variables" value={String(output.numberOfVariables)} />
    <StatTile label="Constant" value={AloftPrice.schema.parse(output.constant).toString(true)} />
    <StatTile
      label="R-squared"
      value={output.modelDiagnostics.rSquared.toFixed(2)}
      quality={getThresholdLabel(
        output.modelDiagnostics.rSquared,
        0.7,
        MODEL_RELIABLE_R_SQUARED_MIN,
      )}
    />
    <StatTile
      label="Standard Error"
      value={output.modelDiagnostics.standardErrorRatio.toFixed(2)}
      quality={getThresholdLabel(
        output.modelDiagnostics.standardErrorRatio,
        0.08,
        MODEL_RELIABLE_STANDARD_ERROR_RATIO_MAX,
        false,
      )}
    />
  </Flex>
);
